import { AxiosResponse } from 'axios';
import api from './client';

/**
 * orderTemplateService — additive write path + read feeds for the order_templates
 * definition store (ADR-0001 §2.5). Backend routes registered manually in app.go:
 *   GET  /order-templates/revisions?name=  → bounded version-picker list (editor)
 *   GET  /order-templates/:id              → one revision incl. schema
 *   GET  /order-templates/published?name=  → currently-published revision incl. schema
 *   POST /order-templates                  → insert immutable revision (+ optional publish)
 *   PUT  /order-templates/:id/publish      → advance the published pointer to :id
 */

// OrderTemplateRevisionListItem — one schema-less row in the editor's version picker.
// versionLabel is null for editor-created revisions (only the seed sets it).
export interface OrderTemplateRevisionListItem {
  id: number;
  name: string;
  type: string;
  revision: number;
  versionLabel: string | null;
  isDraft: boolean;
  isPublished: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface SaveOrderTemplateRevisionRequest {
  name: string;
  type: string;
  schema: Record<string, any>;
  publish?: boolean;
  draft?: boolean;
}

export interface OrderTemplateRevisionResponse {
  id: number;
  name: string;
  revision: number;
  type: string;
  is_draft: boolean;
  // Human version (e.g. "1.5"), set by the publish auto-bump; null for a draft.
  versionLabel: string | null;
}

export interface PublishOrderTemplateResponse {
  ok: boolean;
  id: number;
  name: string;
  revision: number;
}

export interface OrderTemplateByIdResponse {
  id: number;
  name: string;
  revision: number;
  type: string;
  schema: Record<string, any>;
  // Human version of THIS revision (e.g. "1.3"); null for editor drafts. The renderer
  // displays it (questionnaire header + printed PDF) so every form names its version.
  versionLabel: string | null;
  isDraft: boolean;
}

export const orderTemplateService = {
  // GET /order-templates/revisions?name=<company>/<type> → the bounded version-picker
  // list (labeled revisions + published + the single latest draft), schema-less.
  // Admin-gated. The editor fetches a chosen revision's schema via getOrderTemplateById.
  listOrderTemplateRevisions: async (
    name: string,
  ): Promise<AxiosResponse<OrderTemplateRevisionListItem[]>> => {
    return api.get('/order-templates/revisions', { params: { name } });
  },

  // GET /order-templates/:id → the exact (immutable) revision incl. its schema.
  // Used by the renderer to load a bound order's DB-served definition.
  getOrderTemplateById: async (
    id: number,
  ): Promise<AxiosResponse<OrderTemplateByIdResponse>> => {
    return api.get(`/order-templates/${id}`);
  },

  getPublishedOrderTemplate: async (
    name: string,
  ): Promise<AxiosResponse<OrderTemplateByIdResponse>> => {
    return api.get('/order-templates/published', { params: { name } });
  },

  saveOrderTemplateRevision: async (
    data: SaveOrderTemplateRevisionRequest,
  ): Promise<AxiosResponse<OrderTemplateRevisionResponse>> => {
    return api.post('/order-templates', data);
  },

  publishOrderTemplateRevision: async (
    id: number,
  ): Promise<AxiosResponse<PublishOrderTemplateResponse>> => {
    return api.put(`/order-templates/${id}/publish`);
  },
};
